// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Divider, Popover, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// api
import type {
  GetDeviceActivityDto,
  GetDeviceDto,
} from '@/api/generated/recalc/endpoints/v2/devices';
import { useGetDeviceActivityQuery } from '@/api/generated/recalc/endpoints/v2/devices';

// shared components
import StyledBadgeWrapper from '@/components/experimental/StyledBadge';

// relative
import {
  deriveDeviceStatus,
  getLatestDeviceActivity,
} from './deviceStatus.utils';

import { DateTime } from 'luxon';

interface DeviceStatusCellProps {
  device?: GetDeviceDto | null;
  hideLabel?: boolean;
}

function formatIso(isoString?: string | null) {
  if (!isoString) {
    return null;
  }

  return DateTime.fromISO(isoString, { zone: 'Europe/Copenhagen' })
    .setLocale('da')
    .toFormat('dd MMM yyyy HH:mm:ss');
}

function TimestampRow({
  label,
  isoString,
}: {
  label: string;
  isoString?: string | null;
}) {
  const formatted = formatIso(isoString);

  if (!formatted) {
    return null;
  }

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="caption" fontFamily="monospace">
        {formatted}
      </Typography>
    </Stack>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) {
    return null;
  }

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="caption" fontFamily="monospace">
        {value}
      </Typography>
    </Stack>
  );
}

function formatActivityTypeLabel(
  t: ReturnType<typeof useTranslation>['t'],
  activityType?: string | null,
) {
  switch (activityType) {
    case 'Rfu':
      return t('devices.activityTypeRfu', { defaultValue: 'RFU' });
    case 'MBus':
      return t('devices.activityTypeMBus', { defaultValue: 'M-Bus' });
    case 'OptoEye':
      return t('devices.activityTypeOptoEye', { defaultValue: 'OptoEye' });
    default:
      return t('common.notAvailable');
  }
}

function formatActivitySummary(
  t: ReturnType<typeof useTranslation>['t'],
  activity?: GetDeviceActivityDto | null,
) {
  if (!activity) {
    return null;
  }

  const parts = [
    activity.gatewayId != null
      ? t('devices.activityGatewayId', {
          defaultValue: 'Gateway {{id}}',
          id: activity.gatewayId,
        })
      : null,
    activity.sourceDeviceId != null
      ? t('devices.activitySourceId', {
          defaultValue: 'Source {{id}}',
          id: activity.sourceDeviceId,
        })
      : null,
    activity.targetDeviceId != null
      ? t('devices.activityTargetId', {
          defaultValue: 'Target {{id}}',
          id: activity.targetDeviceId,
        })
      : null,
    activity.rssi != null
      ? t('devices.activityRssiValue', {
          defaultValue: '{{value}} dBm',
          value: activity.rssi,
        })
      : null,
  ].filter(Boolean);

  return parts.join(' · ');
}

export function DeviceStatusPanel({ device }: DeviceStatusCellProps) {
  const { t } = useTranslation('units-gateways-sensors');
  const theme = useTheme();
  const status = deriveDeviceStatus(device);
  const latestActivity = getLatestDeviceActivity(device);

  const {
    data: activityHistory,
    isFetching,
    isError,
  } = useGetDeviceActivityQuery(
    {
      deviceId: device?.deviceId ?? 0,
      orderBy: 'lastActivity',
      orderDirection: 'desc',
      top: 5,
      activityType: 'Both',
    },
    { skip: !device?.deviceId },
  );

  const statusColor = {
    online: theme.palette.status.online,
    stale: theme.palette.status.degraded,
    offline: theme.palette.status.offline,
  }[status];

  const statusLabel = {
    online: t('devices.statusOnline', { defaultValue: 'Online' }),
    stale: t('devices.statusStale', { defaultValue: 'Stale' }),
    offline: t('devices.statusOffline', { defaultValue: 'Never connected' }),
  }[status];

  return (
    <Stack spacing={0}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: statusColor }}>
          {statusLabel}
        </Typography>
      </Stack>

      {status === 'stale' && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: 'block', mb: 1 }}
        >
          {t('devices.statusStaleHint', {
            defaultValue:
              'The device has activity, but none has been reported within the last hour.',
          })}
        </Typography>
      )}

      <Divider sx={{ mb: 1 }} />

      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <TimestampRow
          label={t('devices.lastActivity', { defaultValue: 'Last activity' })}
          isoString={latestActivity?.lastActivity}
        />
        <TimestampRow
          label={t('devices.latestSourceActivity', {
            defaultValue: 'Latest source activity',
          })}
          isoString={device?.latestSourceActivity?.lastActivity}
        />
        <TimestampRow
          label={t('devices.latestTargetActivity', {
            defaultValue: 'Latest target activity',
          })}
          isoString={device?.latestTargetActivity?.lastActivity}
        />
      </Stack>

      {latestActivity && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            {t('devices.latestActivityDetails', {
              defaultValue: 'Latest activity details',
            })}
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <InfoRow
              label={t('devices.activityType', {
                defaultValue: 'Activity type',
              })}
              value={formatActivityTypeLabel(t, latestActivity.activityType)}
            />
            <InfoRow
              label={t('devices.gateway', { defaultValue: 'Gateway' })}
              value={
                latestActivity.gatewayId != null
                  ? String(latestActivity.gatewayId)
                  : null
              }
            />
            <InfoRow
              label={t('devices.sourceDeviceId', {
                defaultValue: 'Source device',
              })}
              value={
                latestActivity.sourceDeviceId != null
                  ? String(latestActivity.sourceDeviceId)
                  : null
              }
            />
            <InfoRow
              label={t('devices.targetDeviceId', {
                defaultValue: 'Target device',
              })}
              value={
                latestActivity.targetDeviceId != null
                  ? String(latestActivity.targetDeviceId)
                  : null
              }
            />
            <InfoRow
              label={t('devices.rssi', { defaultValue: 'RSSI' })}
              value={
                latestActivity.rssi != null
                  ? t('devices.activityRssiValue', {
                      defaultValue: '{{value}} dBm',
                      value: latestActivity.rssi,
                    })
                  : null
              }
            />
          </Stack>
        </>
      )}

      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
        {t('devices.recentActivity', { defaultValue: 'Recent activity' })}
      </Typography>
      {isFetching && (
        <Typography variant="body2" color="text.secondary">
          {t('devices.loadingActivity', {
            defaultValue: 'Loading activity history...',
          })}
        </Typography>
      )}
      {isError && (
        <Typography variant="body2" color="error">
          {t('devices.activityLoadError', {
            defaultValue: 'Failed to load device activity history',
          })}
        </Typography>
      )}
      {!isFetching && !isError && (activityHistory?.length ?? 0) === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('devices.noRecentActivity', {
            defaultValue: 'No recent activity found',
          })}
        </Typography>
      )}
      {!isFetching && !isError && (activityHistory?.length ?? 0) > 0 && (
        <Stack spacing={1}>
          {activityHistory?.map((activity, index) => (
            <Stack
              key={`${activity.lastActivity ?? 'activity'}-${index}`}
              spacing={0.25}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {formatActivityTypeLabel(t, activity.activityType)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatActivitySummary(t, activity)}
              </Typography>
              <Typography variant="caption" fontFamily="monospace">
                {formatIso(activity.lastActivity) ?? t('common.notAvailable')}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

const DeviceStatusCell = ({
  device,
  hideLabel = false,
}: DeviceStatusCellProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const status = deriveDeviceStatus(device);
  const statusColor = {
    online: theme.palette.status.online,
    stale: theme.palette.status.degraded,
    offline: theme.palette.status.offline,
  }[status];

  const statusLabel = {
    online: t('devices.statusOnline', { defaultValue: 'Online' }),
    stale: t('devices.statusStale', { defaultValue: 'Stale' }),
    offline: t('devices.statusOffline', { defaultValue: 'Never connected' }),
  }[status];

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        title={statusLabel}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          maxWidth: hideLabel ? 28 : 120,
          minWidth: hideLabel ? 28 : 120,
          justifyContent: hideLabel ? 'center' : 'flex-start',
        }}
      >
        <StyledBadgeWrapper color={statusColor} enableRipple={false} />
        {!hideLabel && <Typography variant="body2">{statusLabel}</Typography>}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, minWidth: 320, maxWidth: 400 } } }}
      >
        {open && <DeviceStatusPanel device={device} />}
      </Popover>
    </>
  );
};

export default DeviceStatusCell;
